import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Users, ShieldCheck, Newspaper, ArrowUpRight } from "lucide-react";
import { HERO_SLIDES } from "./data";
import { api } from "@/lib/api";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fmtDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}

export const Hero = () => {
  const [slides, setSlides] = useState(HERO_SLIDES);
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(0);
  const touch = useRef({ x: 0, y: 0 });

  useEffect(() => {
    api.get("/content/hero").then(({ data }) => {
      const urls = (data || []).map((s) => s.image_url).filter(Boolean);
      if (urls.length) setSlides(urls);
    }).catch(() => {});
    api.get("/posts").then(({ data }) => setPosts((data || []).slice(0, 3))).catch(() => {});
  }, []);

  const next = useCallback(() => setActive((i) => (i + 1) % slides.length), [slides.length]);
  const goTo = (i) => setActive((i + slides.length) % slides.length);

  useEffect(() => {
    const id = setInterval(next, 5500);
    return () => clearInterval(id);
  }, [next]);

  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) goTo(active + (dx < 0 ? 1 : -1));
  };

  return (
    <>
    <section
      id="inicio"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative h-[calc(100svh-4.5rem)] min-h-[560px] w-full overflow-hidden bg-navy"
    >
      {/* Full-bleed image slider */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={active}
            src={slides[active]}
            alt="Club Comandante Leo Nowak"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1 }, scale: { duration: 6, ease: "easeOut" } }}
            onError={(e) => (e.currentTarget.style.display = "none")}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        {/* Legibility gradients + fine finishes */}
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/60 via-navy/10 to-transparent" />
        <div className="absolute inset-0 grain-overlay opacity-30" />
        {/* soft vignette to make slider pop */}
        <div className="absolute inset-0 shadow-[inset_0_0_180px_60px_rgba(7,18,54,0.55)]" />
      </div>

      {/* Docked content (left) */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 sm:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex self-start items-center gap-2 rounded-full bg-white/10 border border-white/25 backdrop-blur-md px-4 py-1.5 mb-5"
        >
          <Sparkles className="h-4 w-4 text-gold" />
          <span className="text-[11px] sm:text-xs font-bold tracking-[0.18em] uppercase text-gold">50° Aniversario · 1976 — 2026</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08 }}
          className="text-white font-black tracking-tighter text-5xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-4xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]"
          style={{ fontFamily: "Outfit" }}
        >
          Club Comandante <span className="text-gold">Leo Nowak</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18 }}
          className="mt-5 text-base sm:text-xl text-white/90 max-w-xl leading-relaxed"
        >
          Medio siglo uniendo generaciones a través del fútbol y la comunidad. Todo el club, en un solo lugar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.28 }}
          className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <Link
            to="/membresias" data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold text-base px-7 py-4 rounded-xl shadow-xl hover:-translate-y-0.5 transition-transform"
          >
            Hacete socio <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/nosotros" data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/35 backdrop-blur-md text-white font-semibold text-base px-7 py-4 rounded-xl hover:bg-white/20 transition-colors"
          >
            Conocé el club
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-2 text-white/90"
        >
          <div className="flex items-center gap-2 text-sm font-medium"><Users className="h-5 w-5 text-gold" /> +800 socios activos</div>
          <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-5 w-5 text-gold" /> 50 años de identidad</div>
        </motion.div>
      </div>

      {/* News ribbon (right) — glassmorphism */}
      {posts.length > 0 && (
        <motion.aside
          initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
          data-testid="hero-news-ribbon"
          className="hidden lg:flex flex-col absolute z-20 top-1/2 -translate-y-1/2 right-8 xl:right-12 w-[340px] xl:w-[380px] rounded-3xl border border-white/15 bg-white/[0.07] backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 pt-5 pb-3">
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gold/15 border border-gold/30">
              <Newspaper className="h-4 w-4 text-gold" />
            </span>
            <div className="leading-tight">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-gold">Novedades</div>
              <div className="text-sm font-bold text-white" style={{ fontFamily: "Outfit" }}>Últimas noticias</div>
            </div>
          </div>

          <div className="px-3 pb-2 space-y-1">
            {posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/noticias/${p.slug}`}
                data-testid={`hero-news-item-${i}`}
                className="group flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-white/10"
              >
                <div className="h-14 w-14 rounded-xl overflow-hidden shrink-0 bg-white/10 border border-white/10">
                  {p.cover_url
                    ? <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    : <div className="w-full h-full flex items-center justify-center text-white/30"><Newspaper className="h-5 w-5" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-gold/90">{fmtDate(p.created_at)}</div>
                  <div className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-gold transition-colors">{p.title}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-gold transition-colors shrink-0" />
              </Link>
            ))}
          </div>

          <Link
            to="/noticias"
            data-testid="hero-news-all"
            className="group mx-3 mb-3 mt-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/5 py-3 text-sm font-bold text-white hover:bg-gold hover:text-navy hover:border-gold transition-colors"
          >
            Ver todas las noticias
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.aside>
      )}

      {/* Slider dots */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5 lg:left-auto lg:translate-x-0 lg:right-[380px] xl:right-[440px]">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-slide-dot-${i}`}
            onClick={() => setActive(i)}
            aria-label={`Ver imagen ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${active === i ? "w-8 bg-gold" : "w-2 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>

    {/* Noticias en mobile: lista simple y sutil, justo debajo del hero */}
    {posts.length > 0 && (
      <div className="lg:hidden bg-[#eef1f7]" data-testid="hero-news-mobile">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.16em] uppercase text-navy/70">
              <Newspaper className="h-4 w-4 text-gold" /> Últimas noticias
            </span>
            <Link to="/noticias" data-testid="hero-news-mobile-all" className="text-xs font-bold text-navy hover:text-gold inline-flex items-center gap-1">
              Ver todas <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="rounded-2xl bg-white border border-navy/5 shadow-sm overflow-hidden divide-y divide-navy/5">
            {posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/noticias/${p.slug}`}
                data-testid={`hero-news-mobile-item-${i}`}
                className="flex items-center gap-3 p-3 active:bg-navy/[0.04] transition-colors"
              >
                <div className="h-12 w-12 rounded-lg overflow-hidden bg-navy/5 shrink-0">
                  {p.cover_url
                    ? <img src={p.cover_url} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-navy/30"><Newspaper className="h-4 w-4" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold text-gold">{fmtDate(p.date || p.created_at)}</div>
                  <div className="text-sm font-semibold text-navy leading-snug line-clamp-2">{p.title}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-navy/30 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    )}
    </>
  );
};
